import {
  Center,
  DarkMode,
  Flex,
  FlexProps,
  IconButton,
  rem,
  Show,
  Spinner,
  Tooltip,
} from '@vimeo/bokeh';
import { ChevronLeft, ChevronRight } from '@vimeo/bokeh-icons';
import { PlayerWrapper } from '@vimeo/clip-ui/components/PlayerWrapper';
import { useLayout } from '@vimeo/clip-ui/hooks/use-layout';
import { EmbedPlayer } from '@vimeo/embed-player';
import { translate } from '@vimeo/i18n-runtime';

import { Clip } from '../utils/types';

interface Props {
  clip: Clip | null;
  hideNav: boolean;
  selectedId: string | null;
  allowContinuousPlay?: boolean;
}

export const IframePlayerModule = ({ allowContinuousPlay, clip, hideNav, selectedId }: Props) => {
  const { isSideBySideActive } = useLayout();

  /** When directly landing on this module, `clip` might be loading */
  if (!selectedId || !clip) return <PlayerLoadingView isSticky={false} />;

  return (
    <PlayerWrapper
      backgroundColor="#000"
      data-autoplay={allowContinuousPlay ? '1' : '0'}
      globalNavigationHidden={hideNav}
      isSticky={false}
    >
      <Player clip={clip} />

      {!isSideBySideActive ? (
        <Show above="xl">
          <PrevNextButtonWrapper>
            <PreviousClip />
            <NextClip />
          </PrevNextButtonWrapper>
        </Show>
      ) : null}
    </PlayerWrapper>
  );
};

export const PrevNextButtonWrapper = ({ children, ...rest }: FlexProps) => (
  <Flex
    position="absolute"
    inset={`0 ${rem(24)} 0 ${rem(24)}`}
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    pointerEvents="none"
    {...rest}
  >
    <DarkMode>{children}</DarkMode>
  </Flex>
);

interface PrevNextClipProps {
  dataHref?: string;
  onClick?: () => void;
}

export const PreviousClip = ({ dataHref, onClick }: PrevNextClipProps) => (
  <Tooltip label={translate('Previous video')}>
    <IconButton
      aria-label={translate('Previous video')}
      data-href={dataHref}
      variant="tertiary"
      size="lg"
      icon={<ChevronLeft />}
      pointerEvents="auto"
      onClick={onClick}
    />
  </Tooltip>
);

export const NextClip = ({ dataHref, onClick }: PrevNextClipProps) => (
  <Tooltip label={translate('Next video')}>
    <IconButton
      aria-label={translate('Next video')}
      data-href={dataHref}
      variant="tertiary"
      size="lg"
      icon={<ChevronRight />}
      pointerEvents="auto"
      onClick={onClick}
    />
  </Tooltip>
);

interface PlayerLoadingViewProps {
  bannerHeight?: string;
  isSticky?: boolean;
}

export const PlayerLoadingView = ({ bannerHeight, isSticky }: PlayerLoadingViewProps) => (
  <PlayerWrapper backgroundColor="#000" outerContainerHeight={bannerHeight} isSticky={isSticky}>
    <Center h="100%">
      <Spinner size="lg" color="white" />
    </Center>
  </PlayerWrapper>
);

interface PlayerProps {
  clip: Clip;
  onEnded?: () => void;
}

export const Player = ({ clip, onEnded }: PlayerProps) => (
  <Flex className="viewer-showcase-player" h="100%">
    {/* Event (live clip) or clips that's non-playable (in transcoding) doesn't have `embedPlayerConfigUrl` and might require an additional API request to get config, we can use a simple iframe here */}
    {clip.eventId || !clip.embedPlayerConfigUrl ? (
      <EmbedPlayer key={`${clip.eventId}-embed`} src={clip.playerEmbedUrl} disableAPI />
    ) : (
      <EmbedPlayer
        title={clip.name}
        src={clip.playerEmbedUrl}
        configUrl={clip.embedPlayerConfigUrl}
        onEnded={onEnded}
      />
    )}
  </Flex>
);
